import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { ShieldCheck, FileText, Lock, Mail } from "lucide-react";
import { Logo } from "@/components/common/Logo";

// Phase 11d — Footer pages. Simple editorial stubs.
function LegalShell({ title, lede, Icon, accent, children, testId }) {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3 min-w-0">
          <Link to="/" className="shrink-0">
            <Logo size={22} />
          </Link>
          <div className="text-[11px] text-slate-500 shrink-0">Updated Feb 2026</div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 min-w-0" data-testid={testId}>
        <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}><Icon size={18} /></div>
        <h1 className="mt-3 text-[26px] sm:text-[32px] font-semibold tracking-tight text-slate-900">{title}</h1>
        <p className="mt-2 text-[14px] text-slate-600 leading-relaxed max-w-2xl">{lede}</p>
        <div className="mt-8 space-y-5 text-[13.5px] text-slate-700 leading-relaxed break-words">
          {children}
        </div>
      </main>
      <footer className="border-t border-slate-200 mt-12 px-4 sm:px-6 py-6 text-center text-[11px] text-slate-500">
        © 2026 Sellyx · <Link to="/" className="hover:text-slate-900">Back to home</Link>
      </footer>
    </div>
  );
}
LegalShell.propTypes = {
  title: PropTypes.string.isRequired,
  lede: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
  accent: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  testId: PropTypes.string.isRequired,
};

const H = ({ children }) => <h2 className="text-[18px] font-semibold tracking-tight text-slate-900 mt-6">{children}</h2>;
H.propTypes = { children: PropTypes.node.isRequired };

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy" lede="Your data stays on your instance. We don't see your conversations, your contacts, or your inbox content." Icon={Lock} accent="bg-indigo-100 text-indigo-700" testId="privacy-page">
      <H>Your data, your instance</H>
      <p>Sellyx runs as a single-tenant container per customer. Your WhatsApp sessions, Gmail OAuth tokens, LinkedIn cookies, conversation history, persona samples, and contact records live on your instance — never on Sellyx-shared infrastructure.</p>
      <H>What we collect</H>
      <p>Account metadata only — your work email, billing details (via Stripe), and aggregated platform telemetry (uptime, error counts, feature usage). We never look at message bodies, contact lists, or any business content.</p>
      <H>Subprocessors</H>
      <p>Anthropic (LLM inference), AWS (private SaaS hosting), Stripe (billing), Resend (transactional email). Anthropic processing happens through a zero-retention API agreement: no model training, no cross-customer mixing, no log retention.</p>
      <H>Data deletion</H>
      <p>You can wipe your instance at any time from Settings → Privacy. Backups are purged within 30 days.</p>
      <H>Contact</H>
      <p>Questions? <Link to="/contact" className="text-indigo-700">Contact our DPO →</Link></p>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service" lede="Plain-English terms. The boring parts, written for humans." Icon={FileText} accent="bg-slate-100 text-slate-700" testId="terms-page">
      <H>Your subscription</H>
      <p>Sellyx is sold as a monthly subscription. You pay per active instance. We don't lock you in — you can cancel anytime from Settings → Billing, and your instance stays up through the end of your billing cycle.</p>
      <H>Acceptable use</H>
      <p>Don't use Maya to spam, scrape platforms in violation of their TOS, or run schemes that could damage your sender reputation in ways our Safety system can't catch. We reserve the right to suspend instances that put our LLM provider relationships at risk.</p>
      <H>Liability</H>
      <p>Sellyx runs on top of your accounts — your WhatsApp, Gmail, LinkedIn. We are not responsible for actions those platforms take against your accounts. Our Safety system catches 99% of risky patterns, but you are still the operator.</p>
      <H>Refunds</H>
      <p>Pro-rated refunds within 14 days of new annual contracts. Monthly plans aren't refundable but you can cancel anytime.</p>
    </LegalShell>
  );
}

export function SecurityPage() {
  return (
    <LegalShell title="Security" lede="How we keep your Maya instance — and the data inside it — safe." Icon={ShieldCheck} accent="bg-emerald-100 text-emerald-700" testId="security-page">
      <H>Encryption</H>
      <p>At-rest encryption with AES-256 on every instance disk. In-transit TLS 1.3 for all API traffic. OAuth tokens, WhatsApp sessions, and persona samples are stored in a per-tenant sealed vault with envelope encryption.</p>
      <H>Single-tenancy</H>
      <p>Every customer gets a private container — own subdomain, own database, own background workers. No multi-tenant SQL queries. No cross-tenant data leakage by design.</p>
      <H>Compliance</H>
      <p>SOC 2 Type II (in audit), GDPR-aligned, ISO 27001 (in progress for 2026). Annual third-party penetration testing.</p>
      <H>Bug bounty</H>
      <p>Found something? Email <span className="font-mono text-indigo-700">security@sellix.ai</span> — we pay $500-$5,000 per validated report.</p>
      <H>Status</H>
      <p>Real-time platform health at <Link to="/status" className="text-indigo-700">status.sellix.ai →</Link></p>
    </LegalShell>
  );
}

export function ContactPage() {
  return (
    <LegalShell title="Talk to us" lede="Real humans answer. No bots, no ticket numbers — just the founding team." Icon={Mail} accent="bg-amber-100 text-amber-700" testId="contact-page">
      <H>Email</H>
      <div className="space-y-2">
        <div className="break-words"><span className="text-slate-500 text-[12px] uppercase tracking-wider">Sales</span> · <span className="font-mono text-indigo-700">hello@sellix.ai</span> · ~2 hours during weekdays</div>
        <div className="break-words"><span className="text-slate-500 text-[12px] uppercase tracking-wider">Support</span> · <span className="font-mono text-indigo-700">support@sellix.ai</span> · ~30 min for paid customers</div>
        <div className="break-words"><span className="text-slate-500 text-[12px] uppercase tracking-wider">Security</span> · <span className="font-mono text-indigo-700">security@sellix.ai</span></div>
        <div className="break-words"><span className="text-slate-500 text-[12px] uppercase tracking-wider">Press</span> · <span className="font-mono text-indigo-700">press@sellix.ai</span></div>
      </div>
      <H>Book a 30-min call</H>
      <p>The fastest way to evaluate. Book a slot with our founding team — we'll show you Maya running on a sandbox instance, and we'll answer the trickiest deployment questions live. <Link to="/demo" className="text-indigo-700">Try the demo →</Link></p>
      <H>Offices</H>
      <p>San Francisco · London · Bengaluru. Hardware appliances ship from our US-West fulfillment partner — ~5 business days door-to-door.</p>
    </LegalShell>
  );
}
